import { useCalendarIntegration } from 'Hooks'
import { IntegrationCard } from 'Pages/ProjectPage/Sections/Integrations/Components/IntegrationCard'
import GoogleCalendarLogo from 'Assets/images/google-calendar.svg'
import { useSearchParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { Loading } from 'Organisms/Loading'
import { ModalSuccess } from 'Organisms/ModalSuccess'
import ModalConfirmOrCancel from 'Organisms/ModalConfirmOrCancel'
import { Plugs } from '@phosphor-icons/react'
import { COLOR_RED } from 'constant/colors'
import { Mixpanel } from 'Utils/Mixpanel'
import { SuggestionPopupToUpgradePlan } from 'Organisms/SuggestionPopupToUpgradePlan'
import { ModalConnectGoogleCalendar } from './Components'

// ? Matches the exact message pulse-backend's calendar service throws (calendar.service.ts,
// selectGoogleCalendars) when a free-plan company tries to sync more than one calendar.
const CALENDAR_SELECTION_LIMIT_MESSAGE = 'Your plan does not allow you to sync more than one calendar.'

export const GOOGLE_CALENDAR_INTEGRATION = {
  id: 'google_calendar',
  logo: GoogleCalendarLogo,
  title: 'Google Calendar',
  description: 'Connect your Google Calendar to see your calendar events alongside your tracked time on the Time page.'
}

const ExternalCalendars = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const authorized = searchParams.get('authorized')

  const [showPicker, setShowPicker] = useState(false)
  const [message, setMessage] = useState({ open: false })
  const [deleteConnection, setDeleteConnection] = useState(false)
  const [visibleProfessionalModal, setVisibleProfessionalModal] = useState(false)

  const {
    loadingCalendarIntegrationHook,
    listGoogleCalendarsHook,
    connectionStatusHook,
    getConnectionStatusHook,
    connectGoogleHook,
    getGoogleCalendarsHook,
    selectGoogleCalendarsHook,
    disconnectGoogleHook
  } = useCalendarIntegration()

  const handleConnect = async () => {
    const res = await connectGoogleHook()

    if (!res.success) {
      return setMessage({ open: true, error: true, message: res.message })
    }

    Mixpanel.track('Settings - Connect Google Calendar')
    window.location.href = res.data
  }

  const handleOpenPicker = async () => {
    const res = await getGoogleCalendarsHook()

    if (!res.success) {
      return setMessage({ open: true, error: true, message: res.message })
    }

    if (!res.data.requires_selection) {
      await getConnectionStatusHook()
      Mixpanel.track('Settings - Connect Google Calendar')
      return setMessage({ open: true, message: 'Your Google Calendar is now connected!' })
    }

    setShowPicker(true)
  }

  const handleSubmitCalendars = async (calendarIds) => {
    const res = await selectGoogleCalendarsHook({ calendarIds })

    if (!res.success) {
      if (res.message === CALENDAR_SELECTION_LIMIT_MESSAGE) {
        return setVisibleProfessionalModal(true)
      }

      return setMessage({ open: true, error: true, message: res.message })
    }

    await getConnectionStatusHook()
    Mixpanel.track('Settings - Select Google Calendars')
    setMessage({ open: true, message: 'Your calendar selection has been saved!' })
  }

  const handleConfirmDisconnect = () => setDeleteConnection(true)

  const handleDisconnect = async () => {
    const res = await disconnectGoogleHook()

    if (!res.success) {
      return setMessage({ open: true, error: true, message: res.message })
    }

    await getConnectionStatusHook()
    Mixpanel.track('Settings - Disconnect Google Calendar')
    setMessage({ open: true, message: 'Google Calendar has been disconnected.' })
  }

  useEffect(() => {
    getConnectionStatusHook()
  }, [])

  useEffect(() => {
    if (!authorized) return

    if (authorized === 'true') {
      handleOpenPicker()
    } else if (authorized === 'error') {
      setMessage({ open: true, error: true, message: 'We couldn’t connect your Google Calendar. Please try again.' })
    }

    // ? This is a one-time OAuth redirect flag - clear it once handled, otherwise it lingers in
    // the URL and re-fires this effect (re-running the calendar picker fetch) on every reload or
    // remount of this page, even for a user who never went through the connect flow themselves.
    searchParams.delete('authorized')
    setSearchParams(searchParams, { replace: true })
  }, [authorized])

  return (
    <>
      {loadingCalendarIntegrationHook && <Loading />}

      <div className="col-12 mb-4">
        <div className="card p-5">
          <IntegrationCard
            logo={GOOGLE_CALENDAR_INTEGRATION.logo}
            title={GOOGLE_CALENDAR_INTEGRATION.title}
            description={GOOGLE_CALENDAR_INTEGRATION.description}
            onConnect={handleConnect}
            integrationInfo={connectionStatusHook.connected ? connectionStatusHook : null}
            onEdit={handleOpenPicker}
            onDelete={handleConfirmDisconnect}
          />
        </div>
      </div>

      {showPicker && (
        <ModalConnectGoogleCalendar
          onClose={() => setShowPicker(false)}
          calendars={listGoogleCalendarsHook}
          onSubmit={handleSubmitCalendars}
        />
      )}

      {deleteConnection && (
        <ModalConfirmOrCancel
          title="Are you sure you want to disconnect Google Calendar?"
          setVisible={() => setDeleteConnection(false)}
          btnConfirm="Disconnect"
          onConfirm={handleDisconnect}
          icon={<Plugs size={56} color={COLOR_RED} weight="bold" />}
          style="danger"
        />
      )}

      {message.open && (
        <ModalSuccess
          visible={true}
          setVisible={() => setMessage({ open: false })}
          title={message.message}
          identifier="modalSuccessExternalCalendars"
          error={message.error}
        />
      )}

      {visibleProfessionalModal && (
        <SuggestionPopupToUpgradePlan
          visible={true}
          setVisible={setVisibleProfessionalModal}
          description="Switch to the professional plan to be able to connect more than one calendar."
        />
      )}
    </>
  )
}

export default ExternalCalendars
